import BadgeForTable from "components/shared-components/BadgeForTable";
import ConfirmModal from "components/shared-components/ConfirmModal";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import HubFilter from "components/shared-components/RemoteTable/HubFilter/HubFilter";
import { ROLE_APP } from "constants/actionAccess";
import { convertDateTime, formatDateTime } from "helpers/utils";
import {
  useActivatePrioritySchedule,
  useDeactivatePrioritySchedule,
  usePriorityScheduleList,
} from "hooks/priority-schedule/usePrioritySchedule";
import jwt_decode from "jwt-decode";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { selectFilter } from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { AUTH_TOKEN } from "store/auth/login/actionTypes";
import DetailPriorityScheduleModal from "./DetailPriorityScheduleModal";
import { MODE_DETAIL, recordStatus, recordStatusMode } from "./utils";

const PRIORITY_SCHEDULE_LIST_MODAL_STYLE = {
  maxWidth: "min(96vw, 1720px)",
  width: "min(96vw, 1720px)",
};

const PriorityScheduleModal = ({ isOpen, toggle, userType: fixedUserType }) => {
  const { t } = useTranslation("prioritySchedule");
  const tUserTypeLabel = key =>
    t(`${fixedUserType}.${key}`, { defaultValue: t(key) });
  const modalListTitle = t("title.list", { target: t("prioritySchedule") });

  const [isLogAdmin, setIsLogAdmin] = useState(false);
  const [idSelected, setIdSelected] = useState(null);

  const [activePriorityScheduleModal, setActivePriorityScheduleModal] = useState(false);
  const toggleActivePriorityScheduleModal = () =>
    setActivePriorityScheduleModal(prev => !prev);

  const [inactivePriorityScheduleModal, setInactivePriorityScheduleModal] = useState(false);
  const toggleInactivePriorityScheduleModal = () =>
    setInactivePriorityScheduleModal(prev => !prev);

  const [detailModal, setDetailModal] = useState(false);
  const toggleDetailModal = () => setDetailModal(prev => !prev);
  const [modeDetail, setModeDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);

  const [priorityScheduleParams, setPriorityScheduleParams] = useState({
    sizePerPage: 10,
    page: 1,
    columnFilter: {
      "filter[user_type]": fixedUserType,
    },
    columnFilterKeep: {},
    sort: "-priority_start_date",
    keywords: "",
    expand: "hub,route,routeLocality,timeSlot,originalUser,replacementUser",
    run: false,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setPriorityScheduleParams(prev => ({
      ...prev,
      columnFilter: { "filter[user_type]": fixedUserType },
      columnFilterKeep: {},
      page: 1,
      keywords: "",
      run: false,
    }));
  }, [isOpen, fixedUserType]);

  const {
    data: priorityScheduleData,
    isLoading: isPriorityScheduleLoading,
    refetch: fetchPrioritySchedule,
  } = usePriorityScheduleList({ params: priorityScheduleParams });

  const { mutate: mutateActivatePrioritySchedule, isLoading: isLoadingActivatePrioritySchedule } =
    useActivatePrioritySchedule({
      onSuccess: () => {
        toggleActivePriorityScheduleModal();
        fetchPrioritySchedule();
      },
      onError: () => {},
    });

  const { mutate: mutateDeactivatePrioritySchedule, isLoading: isLoadingDeactivatePrioritySchedule } =
    useDeactivatePrioritySchedule({
      onSuccess: () => {
        toggleInactivePriorityScheduleModal();
        fetchPrioritySchedule();
      },
      onError: () => {},
    });

  useEffect(() => {
    if (priorityScheduleParams?.run) {
      fetchPrioritySchedule();
    }
  }, [priorityScheduleParams]);

  const rowEventsHandler = {
    onDoubleClick: (e, row) => {
      setDetailData(row);
      setModeDetail(MODE_DETAIL.VIEW);
      toggleDetailModal();
    },
  };

  const onSearchAllHandle = useCallback(value => {
    setPriorityScheduleParams(prev => ({
      ...prev,
      page: 1,
      keywords: value,
    }));
  }, []);

  const onTableChange = (type, { page, sizePerPage, columnFilter }) => {
    setPriorityScheduleParams(prev => ({
      ...prev,
      columnFilter: {
        ...prev.columnFilterKeep,
        "filter[user_type]": fixedUserType,
        ...columnFilter,
        "filter[record_status]": columnFilter?.["filter[record_status]"] || undefined,
      },
      page: page || 1,
      sizePerPage: sizePerPage || 10,
    }));
  };

  const selectOptionsTable = (cell = undefined, name = "") => {
    let options = [];
    switch (name) {
      case "record_status":
        options = recordStatus;
        break;
      default:
        options = [];
        break;
    }
    const select = {};
    options.forEach(item => {
      select[item.key] = t(`table.${name}.${item.key}`);
    });
    if (cell) {
      return select[cell];
    }
    return { ...select };
  };

  const formatOptionsTable = (cell, name = "") => {
    let options = [];
    switch (name) {
      case "record_status":
        options = recordStatus;
        break;
      default:
        options = [];
        break;
    }
    return options.map(
      item =>
        item.key === cell && (
          <BadgeForTable key={item.key} badgeColor={item.badgeColor ? item.badgeColor : ""}>
            {selectOptionsTable(cell, name)}
          </BadgeForTable>
        )
    );
  };

  const styleBadge = { textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" };

  const columnTable = [
      {
        dataField: "id",
        text: t("table.no"),
        formatter: (_cell, _row, index) => `${index + 1}`,
      },
      {
        dataField: "hub_id",
        text: t("table.hub"),
        sort: true,
        formatter: (_cell, row) =>
          `${row?.hub?.name} ${row?.hub?.is_center ? " - Center" : ""}`,
      },
      {
        dataField: "route",
        text: t("table.route"),
        sort: true,
        formatter: (_cell, row) => (
          <div className="d-flex flex-wrap gap-2">
            {row?.routes?.map(item => (
              <div
                title={`${item?.name}`}
                style={{ ...styleBadge }}
                className="badge bg-primary font-size-13"
                key={item?.code}
              >
                {item?.code}
              </div>
            ))}
          </div>
        ),
      },
      {
        dataField: "routeLocality",
        text: t("table.routeLocal"),
        sort: true,
        formatter: (_cell, row) => {
          const routeLocalities = row?.routes?.[0]?.route_localities || [];
          return (
            <div className="d-flex flex-wrap gap-2">
              {routeLocalities?.map(item => (
                <div
                  className="badge bg-info font-size-13"
                  style={{ ...styleBadge }}
                  key={item?.code}
                >
                  {`${item?.district_name} ${item?.ward_name ? `- ${item?.ward_name}` : ""}`}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        dataField: "record_status",
        text: t("table.record_status.name"),
        sort: true,
        align: "center",
        filter: selectFilter({
          placeholder: t("common:placeholder.select", {
            title: t("table.record_status.name"),
          }),
          options: selectOptionsTable(undefined, "record_status"),
        }),
        formatter: (cell) => formatOptionsTable(cell, "record_status"),
      },
      {
        dataField: "start_timeslot",
        text: t("table.startTimeSlotOriginalUser"),
        sort: true,
        formatter: (_cell, row) =>
          `${row?.start_timeslot?.name}`,
      },
      {
        dataField: "end_timeslot",
        text: t("table.endTimeSlotOriginalUser"),
        sort: true,
        formatter: (_cell, row) =>
          `${row?.end_timeslot?.name}`,
      },
      {
        dataField: "priority_date",
        text: t("table.priority_date"),
        sort: true,
        formatter: (_cell, row) => (
          <div className="fw-bold text-primary">
            {`${convertDateTime(row?.priority_start_date, formatDateTime.DD_MM_YYYY, "/")} - ${convertDateTime(row?.priority_end_date, formatDateTime.DD_MM_YYYY, "/")}`}
          </div>
        ),
      },
      {
        dataField: "original_user",
        text: tUserTypeLabel("table.originalUser"),
        sort: true,
        formatter: (_cell, row) => (
          <div className="fw-bold">
            {` ${row?.original_user?.display_name}`}
          </div>
        ),
      },
      {
        dataField: "replacement_user",
        text: tUserTypeLabel("table.replacementUser"),
        sort: true,
        formatter: (_cell, row) => (
          <div className="fw-bold text-primary">
            {` ${row?.replacement_user?.display_name}`}
          </div>
        ),
      },
      {
        dataField: "note",
        text: t("table.note"),
        sort: true,
      },
  ];

  const ALLOW_ACTION = "ALLOW";

  const listRowAction = [
    {
      actionAccess: "",
      icon: <i className="fas fa-pen fa-lg text-primary ms-2" />,
      text: t("actions.edit"),
      conditionVisible: { isAllowAction: [ALLOW_ACTION] },
      onClick: row => {
        setDetailData(row);
        setModeDetail(MODE_DETAIL.EDIT);
        toggleDetailModal();
      },
    },
    {
      icon: <i className="fas fa-lock-open fa-lg text-success" />,
      text: t("actions.active"),
      conditionVisible: {
        record_status: [recordStatusMode.INACTIVE],
        isAllowAction: [ALLOW_ACTION],
      },
      onClick: row => {
        setIdSelected(row.id);
        toggleActivePriorityScheduleModal();
      },
    },
    {
      icon: <i className="fas fa-lock fa-lg text-danger" />,
      text: t("actions.inactive"),
      conditionVisible: {
        record_status: [recordStatusMode.ACTIVE],
        isAllowAction: [ALLOW_ACTION],
      },
      onClick: row => {
        setIdSelected(row.id);
        toggleInactivePriorityScheduleModal();
      },
    },
  ];

  useEffect(() => {
    const info = jwt_decode(localStorage.getItem(AUTH_TOKEN));
    const listRole = info["auth"].split(",");
    setIsLogAdmin(
      listRole.find(item => item.includes(ROLE_APP.ROLE_LOG_ADMIN)) ? true : false || false
    );
  }, []);

  const dataListFormat = useMemo(() => {
    const dataList = priorityScheduleData?.items?.map(item => {
      const currentDate = new Date().setHours(0, 0, 0, 0);
      const priorityStartDate = new Date(item.priority_start_date).setHours(0, 0, 0, 0);
      const isAllowAction = priorityStartDate >= currentDate;
      return {
        ...item,
        isAllowAction: isAllowAction ? ALLOW_ACTION : "",
      };
    });
    return dataList;
  }, [priorityScheduleData]);

  return (
    <Modal
      isOpen={isOpen}
      role="dialog"
      autoFocus
      centered
      toggle={toggle}
      style={PRIORITY_SCHEDULE_LIST_MODAL_STYLE}
    >
      <ModalHeader toggle={toggle}>
        {modalListTitle} ({priorityScheduleData?.meta?.totalCount || 0})
      </ModalHeader>
      <ModalBody>
        <ConfirmModal
          isOpen={activePriorityScheduleModal}
          titleHeader={t("modal.active.title")}
          titleBody={t("modal.active.body")}
          onConfirm={() => mutateActivatePrioritySchedule({ id: idSelected })}
          textBtnConfirm={t("modal.active.agree")}
          textBtnCancel={t("modal.active.cancel")}
          toggle={toggleActivePriorityScheduleModal}
          isDisableButton={isLoadingActivatePrioritySchedule}
        />

        <ConfirmModal
          isOpen={inactivePriorityScheduleModal}
          titleHeader={t("modal.inactive.title")}
          titleBody={t("modal.inactive.body")}
          onConfirm={() => mutateDeactivatePrioritySchedule({ id: idSelected })}
          textBtnConfirm={t("modal.inactive.agree")}
          textBtnCancel={t("modal.inactive.cancel")}
          toggle={toggleInactivePriorityScheduleModal}
          isDisableButton={isLoadingDeactivatePrioritySchedule}
        />

        <DetailPriorityScheduleModal
          modeDetail={modeDetail}
          detailData={detailData}
          isOpenModal={detailModal}
          toggleModal={toggleDetailModal}
          fetchPrioritySchedule={fetchPrioritySchedule}
          fixedUserType={fixedUserType}
        />

        <Row>
          <Col>
            <RemoteDataTable
              keyField="id"
              data={dataListFormat || []}
              columns={columnTable}
              loading={isPriorityScheduleLoading}
              page={priorityScheduleData?.meta?.currentPage}
              onTableChange={onTableChange}
              isShowRowIndex={false}
              totalSize={priorityScheduleData?.meta?.totalCount}
              searchSticky
              sizePerPage={priorityScheduleData?.meta?.perPage}
              onSearchAllHandle={onSearchAllHandle}
              rowEvents={rowEventsHandler}
              listRowAction={listRowAction}
            >
              <div className="d-flex flex-column flex-sm-wrap flex-sm-row mb-md-0 align-items-baseline gap-2">
                <HubFilter
                  isForOrder
                  mainFilterParams={priorityScheduleParams}
                  setMainFilterParams={setPriorityScheduleParams}
                  isNotFilterChildHub={true}
                  isHaveAllOption={isLogAdmin}
                  isFilterAllDefault={true}
                  addressLevel={3}
                />
                <Button
                  color="info"
                  className="btn-label"
                  type="button"
                  onClick={() => {
                    setModeDetail(MODE_DETAIL.CREATE);
                    setDetailData(null);
                    toggleDetailModal();
                  }}
                >
                  <i className="bx bx-plus label-icon"></i>
                  {t("button.create")}
                </Button>
              </div>
            </RemoteDataTable>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default PriorityScheduleModal;
